import type { PdfSectionWithPages } from "@repo/shared/types";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { SectionHeader } from "@/components/ui/section-header";
import {
  DragHandleCell,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { useDragReorder } from "@/hooks/use-drag-reorder";
import {
  useAddPortfolioSubNode,
  useDeleteSection,
  useReorderSections,
} from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { SectionDialog } from "./section-dialog";
import { T } from "./section-list.i18n";

type SectionListProps = {
  templateId: string;
  sections: PdfSectionWithPages[];
  isMultiGoalFlow: boolean;
  onSelectSection: (section: PdfSectionWithPages) => void;
  selectedSectionId?: string | null;
};

export function SectionList({
  templateId,
  sections,
  isMultiGoalFlow,
  onSelectSection,
  selectedSectionId,
}: SectionListProps) {
  const t = useT(T);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<PdfSectionWithPages | null>(null);
  const { confirm, ConfirmationDialog } = useConfirm();
  const deleteSectionMutation = useDeleteSection();
  const reorderMutation = useReorderSections();
  const addPortfolioSubNodeMutation = useAddPortfolioSubNode();

  // QP-150 — a goal section (per_goal) and its bonded portfolio sub-node (per_portfolio) form one
  // group. Only top-level items (globals + goal parents) are draggable; the portfolio child renders
  // nested under its goal and moves with it.
  const topLevelSections = useMemo(() => sections.filter((s) => !s.parentSectionId), [sections]);
  const childByParent = useMemo(() => {
    const map = new Map<string, PdfSectionWithPages>();
    for (const s of sections) {
      if (s.parentSectionId) map.set(s.parentSectionId, s);
    }
    return map;
  }, [sections]);

  const {
    ordered: orderedSections,
    draggedIndex,
    getRowProps,
  } = useDragReorder({
    items: topLevelSections,
    onCommit: async (items) => {
      try {
        // Flatten each group to [goal, portfolio-child] so the persisted order keeps them adjacent.
        const sectionIds = items.flatMap((section) => {
          const child = childByParent.get(section.id);
          return child ? [section.id, child.id] : [section.id];
        });
        await reorderMutation.mutateAsync({ templateId, data: { sectionIds } });
      } catch (err) {
        toast.error(t.error, {
          description: err instanceof Error ? err.message : t.failedToReorder,
        });
        // Re-throw so the hook reverts to the original order
        throw err;
      }
    },
  });

  const handleAddPortfolioSubNode = async (e: React.MouseEvent, goalSectionId: string) => {
    e.stopPropagation();
    try {
      await addPortfolioSubNodeMutation.mutateAsync({ goalSectionId });
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToDelete,
      });
    }
  };

  const handleAddSection = () => {
    setEditingSection(null);
    setSectionDialogOpen(true);
  };

  const handleEditSection = (e: React.MouseEvent, section: PdfSectionWithPages) => {
    e.stopPropagation();
    setEditingSection(section);
    setSectionDialogOpen(true);
  };

  const handleDeleteSection = async (e: React.MouseEvent, section: PdfSectionWithPages) => {
    e.stopPropagation();

    const confirmed = await confirm({
      title: t.deleteSection,
      description: t.deleteSectionConfirmation(section.name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    try {
      await deleteSectionMutation.mutateAsync(section.id);
      toast.success(t.sectionDeletedSuccess);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof Error ? err.message : t.failedToDelete,
      });
    }
  };

  const getScopeLabel = (scope: string) => {
    if (scope === "global") return t.global;
    if (scope === "per_portfolio") return t.perPortfolio;
    return t.perGoal;
  };

  // Count total components across all pages in a section
  const getComponentCount = (section: PdfSectionWithPages) => {
    return section.pages.reduce((total, page) => total + (page.components?.length || 0), 0);
  };

  return (
    <>
      <div className="space-y-6">
        <SectionHeader
          description={
            <>
              {t.sectionsDescription}
              {orderedSections.length > 0 && <span className="block mt-1">{t.dragToReorder}</span>}
            </>
          }
          action={
            <Button
              onClick={handleAddSection}
              disabled={deleteSectionMutation.isPending || reorderMutation.isPending}
            >
              {t.addSection}
            </Button>
          }
        />

        {orderedSections.length === 0 ? (
          <EmptyState action={<Button onClick={handleAddSection}>{t.addSection}</Button>}>
            {t.noSectionsDescription}
          </EmptyState>
        ) : (
          <Table variant="interactive">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.sectionScope}</TableHead>
                <TableHead>{t.pages}</TableHead>
                <TableHead>{t.components}</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orderedSections.map((section, index) => {
                const child = section.scope === "per_goal" ? childByParent.get(section.id) : null;
                const actionsDisabled =
                  deleteSectionMutation.isPending ||
                  reorderMutation.isPending ||
                  addPortfolioSubNodeMutation.isPending;
                return (
                  <Fragment key={section.id}>
                    {/* Top-level row: global section OR goal parent (draggable). */}
                    <TableRow
                      {...getRowProps(index)}
                      onClick={() => onSelectSection(section)}
                      className={`
                        cursor-move
                        ${draggedIndex === index ? "opacity-50" : ""}
                        ${selectedSectionId === section.id ? "bg-accent" : ""}
                      `}
                    >
                      <DragHandleCell />
                      <TableCell className="font-medium">{section.name}</TableCell>
                      <TableCell>
                        <Badge variant={section.scope === "global" ? "secondary" : "outline"}>
                          {getScopeLabel(section.scope)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {section.pages.length}{" "}
                        {section.pages.length !== 1 ? t.pages.toLowerCase() : t.page}
                      </TableCell>
                      <TableCell>
                        {getComponentCount(section)}{" "}
                        {getComponentCount(section) !== 1
                          ? t.components.toLowerCase()
                          : t.component}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleEditSection(e, section)}
                            disabled={actionsDisabled}
                            tooltip={t.editSection}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => handleDeleteSection(e, section)}
                            disabled={actionsDisabled}
                            tooltip={t.delete}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Bonded portfolio sub-node row (rendered under its goal, not draggable). */}
                    {child && (
                      <TableRow
                        onClick={() => onSelectSection(child)}
                        className={`
                          cursor-pointer border-l-2 border-l-muted-foreground/30
                          ${selectedSectionId === child.id ? "bg-accent" : "bg-muted/30"}
                        `}
                      >
                        <TableCell />
                        <TableCell
                          className="font-medium pl-8 text-muted-foreground"
                          title={t.portfolioSubNodeHint}
                        >
                          ↳ {child.name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{t.perPortfolio}</Badge>
                        </TableCell>
                        <TableCell>
                          {child.pages.length}{" "}
                          {child.pages.length !== 1 ? t.pages.toLowerCase() : t.page}
                        </TableCell>
                        <TableCell>
                          {getComponentCount(child)}{" "}
                          {getComponentCount(child) !== 1
                            ? t.components.toLowerCase()
                            : t.component}
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleEditSection(e, child)}
                              disabled={actionsDisabled}
                              tooltip={t.editSection}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => handleDeleteSection(e, child)}
                              disabled={actionsDisabled}
                              tooltip={t.delete}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Goal group with no portfolio sub-node — offer to add one. */}
                    {section.scope === "per_goal" && !child && (
                      <TableRow className="bg-muted/30 border-l-2 border-l-muted-foreground/30">
                        <TableCell />
                        <TableCell colSpan={5} className="pl-8">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={(e) => handleAddPortfolioSubNode(e, section.id)}
                            disabled={actionsDisabled}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            {t.addPortfolioContent}
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <p className="text-sm font-medium">{t.aboutSectionScope}</p>
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>
              <strong>{t.global}</strong> - {t.globalSectionInfo}
            </li>
            <li>
              <strong>{t.perGoal}</strong> - {t.perGoalSectionInfo}
            </li>
          </ul>
        </div>
      </div>

      <SectionDialog
        open={sectionDialogOpen}
        onOpenChange={setSectionDialogOpen}
        templateId={templateId}
        section={editingSection}
        isMultiGoalFlow={isMultiGoalFlow}
      />

      <ConfirmationDialog />
    </>
  );
}
